import { useMemo } from 'react'

import { camelCaseToTitleCase, pascalToSnakeCase } from '@domain-group/javascript-utils'
import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import useComment, { UseCommentPayload, useCommentForm } from '@hooks/useComment'
import useComments from '@hooks/useComments'
import useConversation, { UseConversationPayload } from '@hooks/useConversation'
import useCurrentUser from '@hooks/useCurrentUser'
import useReduxAction from '@hooks/useReduxAction'
import useSubscribers from '@hooks/useSubscribers'
import useSelector from '@hooks/useSelector'

import { getLatestCommentWithAttachment } from '@functions/comment'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'
import { CommentModel, ConversationModel, UserModel } from '@models/types'
import { EntityOptions } from '@redux/modules/module'

const watchEntityKeys = ['conversations']

type FindOrBuildConversationParams = {
  conversationParams: ConversationModel,
  conversationsArray: ConversationModel[],
  currentUser: UserModel,
}

const findOrBuildConversation = (params: FindOrBuildConversationParams) => {
  const {
    conversationsArray, currentUser, conversationParams,
  } = params
  const { subject_id, subject_type } = conversationParams

  const existingConversation = conversationsArray.find(conversation => (
    conversation.subject_id === subject_id && conversation.subject_type === subject_type
  ))

  if (existingConversation) return existingConversation

  return {
    ...conversationParams,
    subscribers: [{
      email: currentUser.email,
      user_id: currentUser.id,
    }],
    title: conversationParams.title || `${camelCaseToTitleCase(subject_type)} #${subject_id}`,
  }
}

type CreateCommentWithConversation = {
  commentParams: Partial<CommentModel>,
  conversation: ConversationModel,
  conversationParams: Partial<ConversationModel>,
  createComment: UseCommentPayload['callbacks']['createComment'],
  createSubjectConversation: UseConversationPayload['callbacks']['createSubjectConversation'],
  requestOptions?: EntityOptions,
}

const createCommentWithConversation = (params: CreateCommentWithConversation) => {
  const {
    commentParams,
    conversation,
    conversationParams,
    createComment,
    createSubjectConversation,
    requestOptions,
  } = params

  // If we have a conversation already AND no new subscribers, we can just create a comment
  // If not we create a conversation for the subject with the first comment

  const updatedComment = {
    commentable_id: conversation.id,
    commentable_type: 'Conversation',
    ...commentParams,
  }

  if (conversation.id && !conversationParams) return createComment(updatedComment, requestOptions)

  const updatedConversation = {
    ...conversation,
    ...conversationParams,
    comment: updatedComment,
  }
  //@ts-expect-error
  return createSubjectConversation(updatedConversation, requestOptions)
}

type UseConversationContainerOptions = {
  comment?: Partial<CommentModel>,
  conversation: ConversationModel,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

function useConversationContainer(options: UseConversationContainerOptions) {
  const {
    comment,
    conversation: conversationParams,
    conversation: {
      subject_id,
      subject_type,
    },
    performHttpRequests,
    requestOptions,
  } = options

  // Load Conversation
  useReduxAction(
    'conversations',
    'loadConversationForSubject',
    {
      entityKey: subject_type + subject_id,
      ...defaultRequestOptions.comment,
      ...defaultRequestOptions.conversation,
      ...requestOptions,
    },
    [subject_id, subject_type],
    {
      dispatchAction: (action, requestOptions) => action(pascalToSnakeCase(subject_type), subject_id, requestOptions),
      // @ts-expect-error TS2322
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  const {
    updatedEntities: { conversations: conversationsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const conversations = useSelector(reduxState => reduxState.entities.conversations)
  const conversationsArray = Object.values(conversations)

  const { creating: creatingConversation, loading } = useSelector(reduxState => reduxState.conversations)
  const { creating: creatingComment } = useSelector(reduxState => reduxState.comments)

  const { currentUser } = useCurrentUser()

  // Find Conversation by subject
  const initConversation = useMemo(() => (
    findOrBuildConversation({
      // @ts-expect-error TS2322
      conversationsArray, currentUser, conversationParams,
    })
  ), [conversationsUpdatedAt, currentUser.id, subject_id, subject_type])

  // @ts-expect-error TS2345
  const conversationPayload = useConversation(initConversation)
  const {
    callbacks: { createSubjectConversation },
    conversation,
  } = conversationPayload

  const subscribersPayload = useSubscribers({
    filters: {
      subscribable_id: conversation?.id,
      subscribable_type: 'Conversation',
    },
  })
  const { filteredSubscribers } = subscribersPayload

  const filteredSubscribersCount = filteredSubscribers.length

  const {
    callbacks: { createComment },
  } = useComment()

  const { filteredComments } = useComments({
    filters: { commentable_id: conversation.id, commentable_type: 'Conversation' },
  })

  const commentForm = useCommentForm({
    ...comment,
    commentable_id: conversation.id,
    commentable_type: 'Conversation',
  })

  const filteredCommentsCount = filteredComments.length

  const commentIds = filteredComments.map(comment => comment.id)
  const comments = useSelector(reduxState => reduxState.entities.comments)

  const latestCommentWithAttachment = getLatestCommentWithAttachment(commentIds, comments)

  return {
    callbacks: {
      createCommentWithConversation: (
        commentParams: Partial<CommentModel>,
        requestOptions?: EntityOptions,
        conversationParams?: Partial<ConversationModel>,
      ) => (
        createCommentWithConversation({
          commentParams,
          conversation,
          // @ts-expect-error TS2322
          conversationParams,
          createComment,
          createSubjectConversation,
          requestOptions,
        })
      ),
    },
    commentForm,
    conversation,
    creating: creatingComment || creatingConversation,
    filteredComments,
    filteredCommentsCount,
    filteredSubscribers,
    filteredSubscribersCount,
    hasFilteredComments: !!filteredCommentsCount,
    hasFilteredSubscribers: !!filteredSubscribersCount,
    latestCommentWithAttachment,
    loading,
  }
}

export default useConversationContainer
